import React, { useState, Dispatch } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import TextField from "../../../components/FormComponents/TextField";
import TextArea from "../../../components/FormComponents/TextArea";
import InputGroup from "../../../components/FormComponents/InputGroup";
import { Form, Container, Icon } from "semantic-ui-react";
import {
  createProfile,
  getCurrentProfile,
} from "../../../actions/profileActions";
import PrimaryButton from "../../../components/Button/PrimaryButton";
import UploadPhoto from "../../../components/UploadPhoto";
import { PreviewPhoto } from "../../../types/siteTypes";
import { FormattedMessage } from "react-intl";
import { errorsSelector, profileSelector } from "../../../utils/selectors";
import styles from "./ManageProfile.module.scss";

const ManageProfile: React.FC = ({
  profile: { profile },
  createProfile,
  getProfile,
}: any) => {
  const navigate = useNavigate();
  const [displaySocialInputs, setDisplaySocialInputs] = useState(false);
  const [formData, setFormData] = useState({
    handle: "",
    company: "",
    website: "",
    location: "",
    bio: "",
    youtube: "",
    twitter: "",
    facebook: "",
    linkedin: "",
    instagram: "",
    pinterest: "",
  });
  const [photo, setPhoto] = useState([{ name: "", url: "" }]);
  const edit = profile?.handle ? true : false;
  const {
    handle,
    company,
    website,
    location,
    bio,
    youtube,
    twitter,
    facebook,
    linkedin,
    instagram,
    pinterest,
  } = formData;

  const onSubmit = (e: any): void => {
    e.preventDefault();
    const profileData = {
      handle,
      company,
      website,
      location,
      bio,
      youtube,
      twitter,
      facebook,
      linkedin,
      instagram,
      pinterest,
    };
    createProfile({ ...profileData, photo: photo[0] }, navigate, edit);
  };

  const onChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onAddPhoto = (pic: PreviewPhoto[]) => {
    setPhoto(pic.length > 0 ? [pic[pic.length - 1]] : []);
  };

  React.useEffect(() => {
    getProfile();
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (profile) {
      setFormData({
        handle: profile.handle || "",
        company: profile.company || "",
        website: profile.website || "",
        location: profile.location || "",
        bio: profile.bio || "",
        instagram: profile.social?.instagram || "",
        youtube: profile.social?.youtube || "",
        facebook: profile.social?.facebook || "",
        twitter: profile.social?.twitter || "",
        pinterest: profile.social?.pinterest || "",
        linkedin: profile.social?.linkedin || "",
      });
      if (profile.photo) {
        setPhoto([profile.photo]);
      }
    }
  }, [profile]);

  return (
    <div className={styles.page}>
      <Container>
        <div className={styles.card}>
          <Link to="/dashboard" className={styles.backLink}>
            <Icon name="arrow left" />
            <FormattedMessage id="site.goBack" defaultMessage="Go back" />
          </Link>

          <h1 className={styles.heading}>
            {edit ? (
              <FormattedMessage id="site.manageProfile.editProfile" defaultMessage="Edit Your Profile" />
            ) : (
              <FormattedMessage id="site.manageProfile.createProfile" defaultMessage="Create Your Profile" />
            )}
          </h1>
          <p className={styles.subheading}>
            <FormattedMessage
              id="site.manageProfile.requiredNote"
              defaultMessage="Fields marked * are required"
            />
          </p>

          <Form onSubmit={onSubmit}>

            {/* ── Basic info ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>
                <FormattedMessage id="manageProfile.section.basic" defaultMessage="Basic info" />
              </p>
              <TextField
                placeholder="* Profile handle"
                name="handle"
                value={handle}
                onChange={onChange}
                info="A unique handle for your profile URL."
              />
              <div className={styles.row2}>
                <TextField
                  placeholder="Company"
                  name="company"
                  value={company}
                  onChange={onChange}
                  info="Name of your company or brand"
                />
                <TextField
                  placeholder="Location"
                  name="location"
                  value={location}
                  onChange={onChange}
                  info="Your city or country"
                />
              </div>
              <TextField
                placeholder="Website"
                name="website"
                value={website}
                onChange={onChange}
                info="Your website address"
              />
              <TextArea
                placeholder="Short Bio"
                name="bio"
                value={bio}
                onChange={onChange}
                info="Tell us a bit about yourself"
              />
            </div>

            {/* ── Profile photo ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>
                <FormattedMessage id="manageProfile.section.photo" defaultMessage="Profile photo" />
              </p>
              <div className={styles.photoSection}>
                {photo !== null && (
                  <UploadPhoto photos={photo} updatePhotoList={onAddPhoto} />
                )}
              </div>
            </div>

            {/* ── Social networks ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>
                <FormattedMessage id="manageProfile.section.social" defaultMessage="Social networks" />
              </p>
              <button
                type="button"
                onClick={() => setDisplaySocialInputs((v) => !v)}
                className={`${styles.socialToggle} ${displaySocialInputs ? styles.active : ""}`}
              >
                <Icon name={displaySocialInputs ? "chevron up" : "plus"} />
                <FormattedMessage
                  id="manageProfile.social.toggle"
                  defaultMessage="Add social network links"
                />
              </button>
              <span className={styles.optionalNote}>
                <FormattedMessage id="manageProfile.social.optional" defaultMessage="optional" />
              </span>

              {displaySocialInputs && (
                <div>
                  <InputGroup placeholder="Twitter Profile URL" name="twitter" icon="twitter" value={twitter} onChange={onChange} />
                  <InputGroup placeholder="Facebook Profile URL" name="facebook" icon="facebook" value={facebook} onChange={onChange} />
                  <InputGroup placeholder="YouTube Profile URL" name="youtube" icon="youtube" value={youtube} onChange={onChange} />
                  <InputGroup placeholder="LinkedIn Profile URL" name="linkedin" icon="linkedin" value={linkedin} onChange={onChange} />
                  <InputGroup placeholder="Instagram Profile URL" name="instagram" icon="instagram" value={instagram} onChange={onChange} />
                  <InputGroup placeholder="Pinterest Profile URL" name="pinterest" icon="pinterest" value={pinterest} onChange={onChange} />
                </div>
              )}
            </div>

            {/* ── Submit ── */}
            <div className={styles.actions}>
              <PrimaryButton
                role="submit"
                text={
                  edit ? (
                    <FormattedMessage id="site.manageProfile.save" defaultMessage="Save changes" />
                  ) : (
                    <FormattedMessage id="site.manageProfile.createProfile" defaultMessage="Create Your Profile" />
                  )
                }
              />
            </div>

          </Form>
        </div>
      </Container>
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  createProfile: (data: any, navigate: any) =>
    dispatch(createProfile(data, navigate)),
  getProfile: () => dispatch(getCurrentProfile()),
});

const mapStateToProps = (state: any) => ({
  profile: profileSelector(state),
  errors: errorsSelector(state),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ManageProfile);
